import React from 'react';
import { AlertCircle, Target, Users, Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const AboutSection = () => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-gradient-to-b from-[#050811] via-[#0a0e27] to-[#050811] py-20">
      {/* Helio Market Algo Introduction */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t.about.title}
          </h2>
          <p className="text-2xl text-cyan-400 font-medium">
            {t.about.tagline}
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 backdrop-blur-sm border border-red-500/20 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="text-red-400" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white">{t.about.problem.cardTitle}</h3>
              </div>
              
              <div className="space-y-4 text-white/80">
                <p className="leading-relaxed">
                  {t.about.problem.cardText}
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{t.about.problem.bullet1}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{t.about.problem.bullet2}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{t.about.problem.bullet3}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 mt-1">•</span>
                    <span>{t.about.problem.bullet4}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 to-orange-500/20 blur-3xl -z-10"></div>
          </div>
          
          <div>
            <div className="inline-block px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full mb-6">
              <span className="text-red-400 text-sm font-semibold">{t.about.problem.badge}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t.about.problem.title1}
              <br />
              <span className="text-red-400">{t.about.problem.title2}</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed">
              {t.about.problem.description}
            </p>
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <div className="inline-block px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-6">
              <span className="text-green-400 text-sm font-semibold">{t.about.solution.badge}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t.about.solution.title1}
              <br />
              <span className="text-green-400">{t.about.solution.title2}</span>
            </h2>
            <p className="text-lg text-white/70 mb-8 leading-relaxed">
              {t.about.solution.description}
            </p>
            <p className="text-md text-cyan-400 italic">
              {t.about.solution.focus}
            </p>
          </div>
          
          <div className="order-1 md:order-2 relative">
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-sm border border-green-500/20 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Target className="text-green-400" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white">{t.about.solution.cardTitle}</h3>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/5 border border-green-500/20 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">{t.about.solution.feature1Title}</h4>
                  <p className="text-white/70 text-sm">{t.about.solution.feature1Desc}</p>
                </div>
                
                <div className="bg-white/5 border border-green-500/20 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">{t.about.solution.feature2Title}</h4>
                  <p className="text-white/70 text-sm">{t.about.solution.feature2Desc}</p>
                </div>
                
                <div className="bg-white/5 border border-green-500/20 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">{t.about.solution.feature3Title}</h4>
                  <p className="text-white/70 text-sm">{t.about.solution.feature3Desc}</p>
                </div>
                
                <div className="bg-white/5 border border-green-500/20 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">{t.about.solution.feature4Title}</h4>
                  <p className="text-white/70 text-sm">{t.about.solution.feature4Desc}</p>
                </div>
              </div>
            </div>
            <div className="absolute -inset-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 blur-3xl -z-10"></div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
            <span className="text-blue-400 text-sm font-semibold">{t.about.whoFor.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.about.whoFor.title1}
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 text-transparent bg-clip-text">
              {t.about.whoFor.title2}
            </span>
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-8 hover:border-blue-500/40 transition-all duration-300">
            <div className="w-16 h-16 bg-blue-500/20 rounded-lg flex items-center justify-center mb-6">
              <Users className="text-blue-400" size={32} />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">{t.about.whoFor.card1Title}</h3>
            <p className="text-white/70 leading-relaxed">
              {t.about.whoFor.card1Desc}
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/40 transition-all duration-300">
            <div className="w-16 h-16 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-cyan-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm1 5h-2v6h5v-2h-3V7z"/>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-4">{t.about.whoFor.card2Title}</h3>
            <p className="text-white/70 leading-relaxed">
              {t.about.whoFor.card2Desc}
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8 hover:border-purple-500/40 transition-all duration-300">
            <div className="w-16 h-16 bg-purple-500/20 rounded-lg flex items-center justify-center mb-6">
              <Award className="text-purple-400" size={32} />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">{t.about.whoFor.card3Title}</h3>
            <p className="text-white/70 leading-relaxed">
              {t.about.whoFor.card3Desc}
            </p>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-xl text-white/80 italic">
            {t.about.whoFor.tagline}
          </p>
        </div>
      </section>

      {/* Why Helio Market Algo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
            <span className="text-cyan-400 text-sm font-semibold">{t.about.why.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.about.why.title}
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-cyan-500/30 transition-all duration-300">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">{t.about.why.feature1Title}</h3>
                <p className="text-white/70 leading-relaxed">
                  {t.about.why.feature1Desc}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-cyan-500/30 transition-all duration-300">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">{t.about.why.feature2Title}</h3>
                <p className="text-white/70 leading-relaxed">
                  {t.about.why.feature2Desc}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-cyan-500/30 transition-all duration-300">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">{t.about.why.feature3Title}</h3>
                <p className="text-white/70 leading-relaxed">
                  {t.about.why.feature3Desc}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:border-cyan-500/30 transition-all duration-300">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">{t.about.why.feature4Title}</h3>
                <p className="text-white/70 leading-relaxed">
                  {t.about.why.feature4Desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutSection;
